import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Package, MapPin, Calendar, Clock, User, Phone,
    Download, FileText, Image as ImageIcon, QrCode, Trash2,
    CheckCircle, XCircle, Truck,
} from 'lucide-react';
import type { Pickup } from '../../../types';
import pickupsAPI from '../../../api/pickup';
import PickupStatusBadge from '../../../components/vendor/PickupStatusBadge';
import { formatDateEnglish } from '../../../utils/date';
import { formatPhoneNumber } from '../../../utils/phoneFormatter';
import { getProductImageUrl } from '../../../utils/productUtilities';
import ConfirmationModal from '../../../components/common/ConfirmationModal';
import toast from '../../../utils/toast';

const PickupDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [pickup, setPickup] = useState<Pickup | null>(null);
    const [loading, setLoading] = useState(true);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const canCancel = pickup?.status === 'draft' || pickup?.status === 'pending';
    const canDelete = pickup?.status === 'draft' || pickup?.status === 'cancelled';
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const data = await pickupsAPI.getPickupRequest(Number(id));
                setPickup(data);
            } catch (err) {
                console.error('Failed to load pickup:', err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id]);

    const handleCancel = async () => {
        setIsCancelling(true);
        try {
            await pickupsAPI.cancelPickup(pickup!.id);
            toast.success('Pickup request cancelled');
            setPickup({ ...pickup!, status: 'cancelled' });
            setShowCancelModal(false);
        } catch (err) {
            console.error('Cancel failed:', err);
        } finally {
            setIsCancelling(false);
        }
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await pickupsAPI.deletePickup(pickup!.id);
            toast.success('Pickup request deleted');
            navigate('/vendor/pickups');
        } catch (err) {
            console.error('Delete failed:', err);
            setIsDeleting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    if (!pickup) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-12 text-center">
                <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Pickup not found</p>
                <button
                    onClick={() => navigate('/vendor/pickups')}
                    className="mt-4 text-sm text-blue-600 hover:text-blue-700"
                >
                    Back to pickups
                </button>
            </div>
        );
    }

    const formatTime = (t?: string | null) => {
        if (!t) return null;
        const [h, m] = t.split(':');
        const hour = parseInt(h, 10);
        const suffix = hour >= 12 ? 'PM' : 'AM';
        const display = hour % 12 || 12;
        return `${display}:${m} ${suffix}`;
    };

    return (
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/vendor/pickups')}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                    <ArrowLeft className="h-5 w-5" />
                </button>
                <div className="flex-1">
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl font-semibold text-gray-900">PU-{pickup.id}</h1>
                        <PickupStatusBadge status={pickup.status} size="md" />
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5 capitalize">
                        {pickup.pickup_type} pickup · created {formatDateEnglish(pickup.created_at)}
                    </p>
                </div>
                <div className="flex gap-2">
                    {canDelete && (
                        <button
                            onClick={() => setShowDeleteModal(true)}
                            className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1.5"
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                        </button>
                    )}
                    {canCancel && (
                        <button
                            onClick={() => setShowCancelModal(true)}
                            className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1.5"
                        >
                            <XCircle className="h-3.5 w-3.5" />
                            Cancel Pickup
                        </button>
                    )}
                </div>
            </div>

            {/* Summary */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
                <h2 className="text-sm font-medium text-gray-700 uppercase tracking-wide">Details</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {pickup.pickup_date && (
                        <div className="flex items-start gap-2">
                            <Calendar className="h-4 w-4 text-gray-400 mt-0.5" />
                            <div>
                                <p className="text-xs text-gray-500">Pickup Date</p>
                                <p className="text-sm text-gray-900">{formatDateEnglish(pickup.pickup_date)}</p>
                            </div>
                        </div>
                    )}
                    {pickup.available_from && (
                        <div className="flex items-start gap-2">
                            <Clock className="h-4 w-4 text-gray-400 mt-0.5" />
                            <div>
                                <p className="text-xs text-gray-500">Available From</p>
                                <p className="text-sm text-gray-900">{formatTime(pickup.available_from)}</p>
                            </div>
                        </div>
                    )}
                    <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                        <div>
                            <p className="text-xs text-gray-500">Pickup Address</p>
                            <p className="text-sm text-gray-900">
                                {[pickup.pickup_address_line_1, pickup.pickup_city_name].filter(Boolean).join(', ')}
                            </p>
                        </div>
                    </div>
                    {pickup.store_names?.length > 0 && (
                        <div className="flex items-start gap-2">
                            <Package className="h-4 w-4 text-gray-400 mt-0.5" />
                            <div>
                                <p className="text-xs text-gray-500">Store(s)</p>
                                <p className="text-sm text-gray-900">{pickup.store_names.join(', ')}</p>
                            </div>
                        </div>
                    )}
                    {pickup.pickup_contact_number_1 && (
                        <div className="flex items-start gap-2">
                            <Phone className="h-4 w-4 text-gray-400 mt-0.5" />
                            <div>
                                <p className="text-xs text-gray-500">Contact</p>
                                <p className="text-sm text-gray-900">
                                    {formatPhoneNumber(pickup.pickup_contact_number_1, { showFlag: false })}
                                    {pickup.pickup_contact_number_2 && (
                                        <span className="block text-gray-500">
                                            {formatPhoneNumber(pickup.pickup_contact_number_2, { showFlag: false })}
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>
                    )}
                    {pickup.driver_name && (
                        <div className="flex items-start gap-2">
                            <User className="h-4 w-4 text-gray-400 mt-0.5" />
                            <div>
                                <p className="text-xs text-gray-500">Driver</p>
                                <p className="text-sm text-gray-900">
                                    {pickup.driver_name}
                                    {pickup.driver_phone && (
                                        <span className="block text-gray-500">{pickup.driver_phone}</span>
                                    )}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Boxes */}
            {pickup.boxes?.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-sm font-medium text-gray-700 uppercase tracking-wide">
                            Boxes ({pickup.boxes.length})
                        </h2>
                    </div>
                    <div className="space-y-2">
                        {pickup.boxes.map(box => (
                            <div key={box.id} className="border border-gray-200 rounded-lg overflow-hidden">
                                {/* header row */}
                                <div className="flex items-center justify-between p-3 bg-gray-50">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-blue-100 rounded">
                                            <QrCode className="h-4 w-4 text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">{box.code}</p>
                                            <p className="text-xs text-gray-500">Box {box.index}</p>
                                        </div>
                                    </div>
                                    {box.label_generated_at && (
                                        <div className="flex gap-3">
                                            {box.png_url && (
                                                <a href={box.png_url} download className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                                                    <ImageIcon className="h-3.5 w-3.5" /> PNG
                                                </a>
                                            )}
                                            {box.pdf_url && (
                                                <a href={box.pdf_url} download className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                                                    <FileText className="h-3.5 w-3.5" /> PDF
                                                </a>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* items inside box */}
                                {box.items?.length > 0 && (
                                    <div className="p-3 space-y-1.5">
                                        {box.items.map(bi => (
                                            <div key={bi.id} className="flex items-center gap-3 text-sm">
                                                <div className="flex-shrink-0 w-9 h-9 rounded overflow-hidden border border-gray-200 bg-gray-50">
                                                    {bi.item_detail?.image_url ? (
                                                        <img
                                                            src={getProductImageUrl(bi.item_detail.image_url)}
                                                            alt=""
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <Package className="h-4 w-4 text-gray-300" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-gray-900 truncate">
                                                        {bi.item_detail?.product_title ?? `Item #${bi.item}`}
                                                    </p>
                                                    {bi.item_detail?.variant_name && (
                                                        <p className="text-xs text-gray-500 truncate">{bi.item_detail.variant_name}</p>
                                                    )}
                                                </div>
                                                <span className="text-gray-700 whitespace-nowrap">× {bi.quantity}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Photos */}
            {pickup.photos?.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <h2 className="text-sm font-medium text-gray-700 uppercase tracking-wide mb-3">
                        Photos ({pickup.photos.length})
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {pickup.photos.map(photo => (
                            <a
                                key={photo.id}
                                href={photo.image}
                                target="_blank"
                                rel="noreferrer"
                                className="aspect-square rounded-lg overflow-hidden border border-gray-200 hover:opacity-90 transition-opacity"
                            >
                                <img src={photo.image} alt="" className="w-full h-full object-cover" />
                            </a>
                        ))}
                    </div>
                </div>
            )}

            {/* Items */}
            {pickup.items?.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <h2 className="text-sm font-medium text-gray-700 uppercase tracking-wide mb-3">
                        Items ({pickup.items.length})
                    </h2>
                    <div className="space-y-2">
                        {pickup.items.map(item => {
                            const imageUrl = item.variant_detail?.image_url || item.product_detail?.image_url;
                            return (
                                <div key={item.id} className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                                        {imageUrl ? (
                                            <img src={getProductImageUrl(imageUrl)} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <Package className="h-5 w-5 text-gray-300" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-gray-900 truncate">
                                            {item.product_detail?.title ?? `Product #${item.product}`}
                                        </p>
                                        {item.variant_detail && (
                                            <p className="text-xs text-gray-500">
                                                {item.variant_detail.name} · SKU {item.variant_detail.sku_suffix}
                                            </p>
                                        )}
                                    </div>
                                    <span className="text-sm text-gray-700">× {item.quantity}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            <ConfirmationModal
                isOpen={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onConfirm={handleCancel}
                title="Cancel Pickup Request?"
                message={`Are you sure you want to cancel PU-${pickup.id}? This cannot be undone.`}
                confirmText="Yes, Cancel Pickup"
                cancelText="Keep"
                type="warning"
                isProcessing={isCancelling}
            />
            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete Draft?"
                message={`This will permanently delete PU-${pickup.id} and its labels. This cannot be undone.`}
                confirmText="Delete"
                cancelText="Keep"
                type="danger"
                isProcessing={isDeleting}
            />
        </div>
    );
};

export default PickupDetailPage;